import React, { useState, useEffect, useRef } from 'react';
import { IAppContext, IAppProviderProps } from './interfaces';
import { IDialogProps, Overlay, SpinnerSize, Text } from '@fluentui/react';
import { IConfirmationDialogProps } from '../../components/dialogs/ConfirmationDialog/interfaces';
import { IFormDialogProps } from '../../components/dialogs/FormDialog/interfaces';
import { ISpaConfiguration, SpaConfiguration } from '@configuration/SpaConfiguration';
import { AppModule } from '@configuration/AppModule';
import { AppComponents } from '@configuration/AppComponents';
import MainLoading from '../../components/loadings/MainLoading';
import { Authentication, AuthenticationSource } from '@app/classes/Authentication';
import BotProtectionVerification, { BotProtectionVerificationProps, BotProtectionVerificationRef } from '../../components/utilities/BotProtectionVerification';
import { getClient, loadOfficeJs, isDev, updateGlobalContext } from '@app/Functions';
import { IXrmGlobalNotification } from '../../Xrm/interfaces/IXrmGlobalNotification';
import { IGlobalNotification } from '@pages/Layout/components/GlobalNotification/interfaces/IGlobalNotification';
import Loading from '../../components/loadings/Loading';
import { SitemapDefinition } from '@definitions/SitemapDefinition';
import ErrorDialog from '../../components/dialogs/ErrorDialog';
import { IErrorDialogProps } from '../../components/dialogs/ErrorDialog/interfaces';
import { EnvironmentNotSelectedException } from '@app/classes/exceptions/EnvironmentNotSelectedException';
import { Exception } from '@app/classes/exceptions/Exception';
import EnvironmentSelectionDialog from '../../components/dialogs/EnvironmentSelectionDialog';
import DevKit from '@src/components/utilities/DevKit';
import { AppSwitcherDialog } from '../../components/dialogs/AppSwitcherDialog';
import { ThemeDefinition } from '@definitions/ThemeDefinition';
import { ILookupPanelProps } from '@src/components/navigation/panels/LookupPanel';
import { WebsiteDefinition } from '@src/app/classes/definitions/WebsiteDefinition';
import { ManifestDefinition } from '@src/app/classes/definitions/ManifestDefinition';
import { PortalWebApiError } from '@src/ComponentFramework/PropertyClasses/WebApi';
import ConfirmationDialog from '@src/components/dialogs/ConfirmationDialog';
import AlertDialog from '@src/components/dialogs/AlertDialog';
import { TransactionCurrencyDefinition } from '@src/app/classes/definitions/TransactionCurrencyDefinition';
import { UserSettingsDefinition } from '@src/app/classes/definitions/UserSettingsDefinition';

export const AppContext = React.createContext<IAppContext | null>(null);

const AppProvider: React.FC<IAppProviderProps> = (props) => {
    const appModuleManifest = ManifestDefinition.getLocallyStoredManifestForCurrentAppModule();
    const [appSwitcherDialogVisible, setAppSwitcherDialogVisible] = useState<boolean>(false);
    const [applicationId, setApplicationId] = useState<string>(null);
    const [refreshSiteMap, setRefreshSiteMap] = useState<boolean>(false);
    const [isNavbarLoaded, setIsNavbarLoaded] = useState<boolean>(false);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isPanelLoading, setPanelLoading] = useState<boolean>(false);
    const [menuExtended, setMenuExtended] = useState<boolean>(false);
    const [errorDialogProps, setErrorDialogProps] = useState<IDialogProps>(null);
    const [verificationProps, setVerificationProps] = useState<BotProtectionVerificationProps>(null);
    const [confirmationDialogProps, setConfirmationDialogProps] = useState<IConfirmationDialogProps>(null);
    const [formDialogs, setFormDialogs] = useState<IFormDialogProps[]>([]);
    const [alertDialogProps, setAlertDialogProps] = useState<IDialogProps>(null);
    const [environmentSelectionDialogProps, setEnvironmentSelectionDialogProps] = useState<IDialogProps>(null);
    const [navigationVisible, setNavigationVisibility] = useState<boolean>(null);
    const [globalNotifications, setGlobalNotifications] = useState<IGlobalNotification[]>([]);
    const [configurationLoaded, setConfigurationLoaded] = useState<boolean>(false);
    const [applicationLoaded, setApplicationLoaded] = useState<boolean>(false);
    const [shouldRefreshMain, setShouldRefreshMain] = useState<boolean>(false);
    const [progressIndicatorMessage, setProgressIndicatorMessage] = useState<string>(null);
    const [showDevelopmentModeButton, setShowDevelopmentModeButton] = useState(false);
    const [connectedEnvironmentDetails, setConnectedEnvironmentDetails] = useState<{ metadataServiceUrl: string, backendUrl: string, environmentUrl: string }>(null);
    const [lookupPanelProps, setLookupPanelProps] = useState<ILookupPanelProps>(null);

    const BotProtectionRef = useRef<BotProtectionVerificationRef>(null);

    let _spaConfiguration: ISpaConfiguration;
    const globalNotificationsRef = useRef<IGlobalNotification[]>([]);

    const addGlobalNotification = async (notification: IXrmGlobalNotification): Promise<string> => {
        const id = window.self.crypto.randomUUID();
        const globalNotification: IGlobalNotification = {
            notification: notification,
            id: id
        };

        globalNotificationsRef.current.push(globalNotification);

        setGlobalNotifications([...globalNotificationsRef.current]);

        return id;
    };

    const clearGlobalNotification = async (id: string): Promise<void> => {
        let newGlobalNotifications = globalNotificationsRef.current.filter(x => x.id !== id);

        globalNotificationsRef.current = newGlobalNotifications;

        setGlobalNotifications([...globalNotificationsRef.current]);
    };

    const closeFormDialog = (formId?: string, entity?: ComponentFramework.WebApi.Entity, dialogInstanceId?: string, isNewRecord = true) => {
        setFormDialogs((currentFormDialogs) => {
            const newFormDialogs = [...currentFormDialogs];
            if (formId) {
                const dialogFormNames = currentFormDialogs.map(x => x.id);
                const indexOfLastDialogWithMatchingFormName = dialogFormNames.lastIndexOf(formId);
                if (indexOfLastDialogWithMatchingFormName > -1) {
                    const closedDialog = newFormDialogs.splice(indexOfLastDialogWithMatchingFormName, 1);
                    if (closedDialog.length === 1) {
                        const dialog = closedDialog[0];
                        dialog.onCloseCallback?.apply(null, isNewRecord ? [{ parameters: entity }] : null);
                    }
                    return newFormDialogs;
                }
            }
            else if (dialogInstanceId) {
                return currentFormDialogs.filter(x => x.id !== dialogInstanceId);
            }
            // In other cases remove the last dialog
            const dialogToClose = newFormDialogs.pop();
            dialogToClose?.onCloseCallback?.apply(null, isNewRecord ? [{ parameters: entity }] : null);

            return newFormDialogs;
        });
    };

    const closeAllFormDialogs = () => {
        setFormDialogs([]);
    };

    const openFormDialog = (dialogProps: IFormDialogProps) => {
        if (!dialogProps.id) {
            dialogProps.id = window.self.crypto.randomUUID();
        }
        setFormDialogs((currentFormDialogs) => [...currentFormDialogs, dialogProps]);
    };

    const setVerificationDialogProps = () => {
        setVerificationProps({
            tokenServiceApi: SpaConfiguration.get().securityTokenServiceApi
        });
    };

    const getVerificationToken = async (): Promise<string> => {
        if (!BotProtectionRef.current) {
            console.warn("BotProtectionVerification component is not mounted, loading it into the page.");
            setVerificationDialogProps();
            const promise = new Promise<string>((resolve, reject) => {
                const intervalId = setInterval(() => {
                    if (BotProtectionRef.current) {
                        clearInterval(intervalId);
                        resolve(BotProtectionRef.current.getVerificationToken());
                    }
                }, 100);
            });
            return promise;
        }
        else {
            return BotProtectionRef.current.getVerificationToken();
        }
    };

    const initAsync = async () => {
        try {
            if (isDev()) {
                setShowDevelopmentModeButton(true);
            }

            window.addEventListener("unhandledrejection", async function (promiseRejectionEvent) {
                console.error(`Unhandled rejection in promise!`, promiseRejectionEvent.reason, promiseRejectionEvent);
                if (typeof promiseRejectionEvent.reason === "object") {
                    let details = promiseRejectionEvent.reason.stack;
                    if (promiseRejectionEvent.reason?.name === "PortalWebApiError") {
                        const reason = promiseRejectionEvent.reason as PortalWebApiError;
                        details = `${reason.details}\n\n---\n\n${details}`;
                    }
                    this.Xrm.Navigation.openErrorDialog({
                        message: promiseRejectionEvent.reason.message,
                        details: details
                    });
                }
                else {
                    this.Xrm.Navigation.openErrorDialog({
                        message: promiseRejectionEvent.reason,
                        details: "No more information provided, look into F12 console for more details."
                    });
                }
            });

            await SpaConfiguration.loadAsync();
            _spaConfiguration = SpaConfiguration.get();
            setConnectedEnvironmentDetails(
                {
                    backendUrl: _spaConfiguration.edsApi,
                    metadataServiceUrl: _spaConfiguration.metadataApi,
                    environmentUrl: _spaConfiguration.instanceUrl
                }
            );
        }
        catch (error) {
            console.error(error);

            let exception = (error instanceof Exception) ? (error as Exception) : new Exception((error as Xrm.Async.ErrorCallbackObject).message);

            // DevBox and Dev Defaults env selection dialog
            if (exception.GetInnerMostException() instanceof EnvironmentNotSelectedException) {
                setEnvironmentSelectionDialogProps({
                    hidden: false
                });
                return;
            }

            const dialogProps: IErrorDialogProps = {
                hidden: false,
                modalProps: {
                    isBlocking: false // Allow users to disconnect from sandbox
                },
                dialogContentProps: {
                    title: window.TALXIS.Portal.Translations.getLocalizedString("@providers/AppProvider/bootError-title"),
                    subText: window.TALXIS.Portal.Translations.getLocalizedString("@providers/AppProvider/bootError-configurationError"),
                    cancelButtonLabel: window.TALXIS.Portal.Translations.getLocalizedString("@providers/AppProvider/bootError-refresh"),
                    moreDetailsText: exception.Print()
                },
                onDismiss: () => {
                    window.location.reload();
                }
            };
            setErrorDialogProps(dialogProps);
            return;
        }
        setConfigurationLoaded(true);
        try {
            if (getClient() === "Outlook") {
                await loadOfficeJs();
            }
            const authenticationResult = await Authentication.trySilentLoginAsync();
            await UserSettingsDefinition.load(Authentication.getUser()?.accessPrincipalId);
            await WebsiteDefinition.loadWebsite();
            await TransactionCurrencyDefinition.load();
            //module not selected, try to show app switcher or load single found module
            if (!await AppModule.loadAsync()) {
                const allAppModules = await AppModule.getAllAppModules();
                if (allAppModules.length > 1) {
                    setAppSwitcherDialogVisible(true);
                    return;
                }
                //load the single found app module
                await AppModule.loadAsync(allAppModules[0].uniquename);
            }
            await ThemeDefinition.loadTheme();
            ManifestDefinition.createManifest(ThemeDefinition.get(), AppModule.get());
            setApplicationId(AppModule.get().appmoduleid);
            await AppComponents.loadAsync();
            await SitemapDefinition.loadCurrentSitemapAsync();

            // Only trigger bot verification when we are not authenticated and the app module has at least one static page that does not require authentication
            const staticPageRoutes = await SitemapDefinition.getStaticPageRoutes();
            if ((!authenticationResult && staticPageRoutes.find(x => x.requiresAuthentication === false)) || Authentication.getAuthenticationSource() === AuthenticationSource.TokenInQuery) {
                setVerificationDialogProps();
            }

            setApplicationLoaded(true);
        }
        catch (error) {
            console.error(error);
            //clear the local storage so user can switch to different app on error
            localStorage.removeItem('appModuleName');
            let exception = (error instanceof Exception) ? (error as Exception) : new Exception((error as Xrm.Async.ErrorCallbackObject).message);
            const dialogProps: IErrorDialogProps = {
                hidden: false,
                modalProps: {
                    isBlocking: false // Allow users to disconnect from sandbox
                },
                dialogContentProps: {
                    title: window.TALXIS.Portal.Translations.getLocalizedString("@providers/AppProvider/bootError-title"),
                    subText: window.TALXIS.Portal.Translations.getLocalizedString("@providers/AppProvider/bootError-authenticationError"),
                    cancelButtonLabel: window.TALXIS.Portal.Translations.getLocalizedString("@providers/AppProvider/bootError-signOut"),
                    moreDetailsText: exception.Print()
                },
                onDismiss: () => {
                    Authentication.logout();
                }
            };
            setErrorDialogProps(dialogProps);
        }
    };

    useEffect(() => {
        initAsync();
    }, []);

    useEffect(() => {
        if (appSwitcherDialogVisible) {
            setLoading(false);
        }
    }, [appSwitcherDialogVisible]);

    return (
        <div>
            {/*  <PWAPrompt promptOnVisit={1} timesToShow={3} copyClosePrompt="Close" permanentlyHideOnDismiss={false}/> */}
            {configurationLoaded &&
                <AppContext.Provider
                    value={{
                        applicationId: applicationId,
                        refreshSiteMap: refreshSiteMap,
                        isNavbarLoaded: isNavbarLoaded,
                        isLoading: isLoading,
                        isPanelLoading: isPanelLoading,
                        menuExtended: menuExtended,
                        errorDialogProps: errorDialogProps,
                        confirmationDialogProps: confirmationDialogProps,
                        formDialogs: formDialogs,
                        alertDialogProps: alertDialogProps,
                        environmentSelectionDialogProps: environmentSelectionDialogProps,
                        navigationVisible: navigationVisible,
                        configurationLoaded: configurationLoaded,
                        shouldRefreshMain: shouldRefreshMain,
                        globalNotifications: globalNotifications,
                        lookupPanelProps: lookupPanelProps,
                        setNavigationVisibility: setNavigationVisibility,
                        setAlertDialogProps: setAlertDialogProps,
                        setEnvironmentSelectionDialogProps: setEnvironmentSelectionDialogProps,
                        setConfirmationDialogProps: setConfirmationDialogProps,
                        getVerificationToken: getVerificationToken,
                        openFormDialog: openFormDialog,
                        closeFormDialog: closeFormDialog,
                        closeAllFormDialogs: closeAllFormDialogs,
                        setErrorDialogProps: setErrorDialogProps,
                        SetLoading: setLoading,
                        SetPanelLoading: setPanelLoading,
                        setMenuExtended: setMenuExtended,
                        setIsNavbarLoaded: setIsNavbarLoaded,
                        setShouldRefreshMain: setShouldRefreshMain,
                        addGlobalNotification: addGlobalNotification,
                        clearGlobalNotification: clearGlobalNotification,
                        showProgressIndicator: (message: string) => {
                            setProgressIndicatorMessage(message);
                        },
                        closeProgressIndicator: () => {
                            setProgressIndicatorMessage(null);
                        },
                        setSitemapItemDisabled: (id: string, disabled: boolean, message?: string) => {
                            const siteMap = SitemapDefinition.getCurrentSiteMap();
                            siteMap.disableSiteMapSubArea(id, disabled, message);
                            setRefreshSiteMap(!refreshSiteMap);
                        },
                        setSitemapItemVisible: (id: string, visible: boolean) => {
                            SitemapDefinition.getCurrentSiteMap().toggleSiteMapSubAreaVisibility(id, visible);
                            setRefreshSiteMap(!refreshSiteMap);
                        },
                        setSitemapGroupVisible: (id: string, visible: boolean) => {
                            SitemapDefinition.getCurrentSiteMap().toggleSiteMapGroupVisibility(id, visible);
                            setRefreshSiteMap(!refreshSiteMap);
                        },
                        setSitemapAreaDisabled: (id: string, disabled: boolean, message?: string) => {
                            const siteMap = SitemapDefinition.getCurrentSiteMap();
                            siteMap.disableSiteMapArea(id, disabled, message);
                            setRefreshSiteMap(!refreshSiteMap);
                        },
                        setSitemapAreaVisible: (id: string, visible: boolean) => {
                            SitemapDefinition.getCurrentSiteMap().toggleSiteMapAreaVisibility(id, visible);
                            setRefreshSiteMap(!refreshSiteMap);
                        },
                        setLookupPanelProps: setLookupPanelProps

                    }}>
                    {!applicationLoaded &&
                        <AppContext.Consumer>
                            {((context) => {
                                updateGlobalContext(context);
                                return (<></>);
                            })}
                        </AppContext.Consumer>}
                    {applicationLoaded && props.children}
                </AppContext.Provider>
            }
            <ErrorDialog {...errorDialogProps} />
            <ConfirmationDialog {...confirmationDialogProps} />
            <AlertDialog {...alertDialogProps} />
            {verificationProps && <BotProtectionVerification ref={BotProtectionRef} {...verificationProps} />}
            <EnvironmentSelectionDialog {...environmentSelectionDialogProps} />
            <MainLoading
                isLoading={!isNavbarLoaded}
                loadingText={appModuleManifest?.manifest.name}
                loadingImageSrc={appModuleManifest?.baseAppModuleIcon} />

            {
                progressIndicatorMessage !== null &&
                <Overlay styles={{
                    root: {
                        background: '#808080ad',
                        top: '0',
                        bottom: '0',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: 'white',
                        left: '0',
                        verticalAlign: 'middle',
                        position: 'fixed',
                        right: '0',
                        margin: 0,
                        textAlign: 'center',
                        zIndex: 1000010
                    }
                }}>
                    <div style={{ opacity: 1 }}>
                        <Loading size={SpinnerSize.large} styles={{ root: { marginBottom: 20 } }} />
                        <Text styles={{ root: { fontWeight: 'bold', color: 'white' } }}>{progressIndicatorMessage}</Text>
                    </div>
                </Overlay>
            }

            {
                appSwitcherDialogVisible &&
                <AppSwitcherDialog appSwitcherDialogVisible={appSwitcherDialogVisible} setAppSwitcherDialogVisible={setAppSwitcherDialogVisible} />
            }
            {
                showDevelopmentModeButton &&
                <DevKit connectedEnvironmentDetails={connectedEnvironmentDetails} />
            }
        </div>
    );
};
export default AppProvider;